/* eslint-disable */
import React, { memo, useEffect, useMemo, useState } from 'react';
import { Controller, useForm, UseFormReturn } from 'react-hook-form';
import useYupValidationResolver from 'src/hooks/useYupValidationResolver';
import * as yup from 'yup';
import RHFInput from 'src/components/elements/Input/RHFInput';
import { Button, Chip, Popover, Rating } from '@mui/material';
import { twMerge } from 'tailwind-merge';

import { RangeDatePicker } from 'src/components';
import { enUS } from 'date-fns/locale';
import RHFMultiSelectChip from 'src/components/elements/MultiSelectChip/RHFMultiSelectChip';
import { BiChevronDown } from 'react-icons/bi';
import { MdClear } from 'react-icons/md';
import { DateRange } from 'src/components/RangeDatePicker/types';
import moment from 'moment';
import RHFMultiAutoComplete from 'src/components/elements/MultiAutoComplete/RHFMultiAutoComplete';
import RHFAutoComplete from 'src/components/elements/AutoComplete/RHFAutoComplete';
import { useClients } from 'src/hooks/query/clients';
import { useRecruiters } from 'src/hooks/query/recruiters';
import { useJobTitles } from 'src/hooks/query/job-titles';
import { useLocations } from 'src/hooks/query/locations';
import { useJobs } from 'src/hooks/query/jobs';
import { useConsultancies } from 'src/hooks/query/consultancies';
import RHFSelect from 'src/components/elements/Select/RHFSelect';
import { useStatuses } from 'src/hooks/query/statuses';
import { useSources } from 'src/hooks/query/source';
import { useJobTypes } from 'src/hooks/query/job-types';
import { TalentPoolTableFilters } from 'src/pages/TalentPool/types';
interface PopoverElementProps {
  handleClose: (element: string) => void,
  open: boolean;
  anchorEl: Element | null;
  element: string;
  children: React.ReactNode;
}

const PopoverElement = (props: PopoverElementProps) => {
  const { open, anchorEl, handleClose, element, children } = props;
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={() => handleClose(element)}
      className="pointer-events-none"
      classes={{
        paper: "pointer-events-auto"
      }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        onMouseLeave: () => handleClose(element)
      }}
    >
      <div className="p-2 min-w-[160px] bg-white max-w-lg">
        {children}
      </div>
    </Popover>
  )
}

const validationSchema = yup.object().shape({
  searchQuery: yup.string(),
});

export type TalentPoolFiltersFormValues = {
  searchQuery: string;
  clients: any;
  dateRange: DateRange | undefined;
  jobs: any;
  source: any;
  title: any;
  email: string;
  consultancy: any;
  location: any;
  recruiter: any;
  status: any;
  scores: {
    pannaScore: {
      min: number | undefined;
      max: number | undefined;
    },
    pannaScoreLive: {
      min: number | undefined;
      max: number | undefined;
    },
  }
};

export type TalentPoolFiltersSubmitHandler = {
  values: TalentPoolFiltersFormValues;
  formInstance: UseFormReturn<TalentPoolFiltersFormValues, any>;
};

interface TalentPoolFiltersProps {
  defaultValue?: TalentPoolTableFilters;
  onSubmit: (data: TalentPoolFiltersSubmitHandler) => void;
  onReset: () => void;
}

const TalentPoolFilters = function TalentPoolFilters(props: TalentPoolFiltersProps) {
  const {
    defaultValue,
    onSubmit,
    onReset,
  } = props;

  const pannaScoreOptions = useMemo(() => {
    return Array.from({length: 11}, (_, i) => i * 10).map((value) => ({ label: value, value }));
  }, []);
  const {
    clientOptions,
    clientsLoading,
    fetchClients,
  } = useClients({ enabled: false });


  const {
    recruitersOptions,
    recruitersLoading,
    fetchRecruiters,
  } = useRecruiters({ enabled: false });

  const {
    jobTypesOptions,
    jonTypesLoading,
    fetchJobTypes,
  } = useJobTypes({ enabled: false });

  const {
    locationsLoading,
    locationsOptions,
    fetchLocations,
  } = useLocations({ enabled: false });

  const {
    jobsLoading,
    jobsOptions,
    fetchJobs,
  } = useJobs({ enabled: false });

  const {
    consultanciesLoading,
    consultanciesOptions,
    fetchConsultancies,
  } = useConsultancies({ enabled: false });

  const {
    statusesLoading,
    statusesOptions,
    fetchStatuses,
  } = useStatuses({ enabled: false });

  const {
    sourcesLoading,
    sourcesOptions,
    fetchSources,
  } = useSources({ enabled: false });

  const resolver = useYupValidationResolver(validationSchema);
  const methods = useForm<TalentPoolFiltersFormValues>({
    defaultValues: {
    },
    resolver,
  });
  const {
    handleSubmit,
    setValue,
  } = methods;

  const submitHandler = (data: TalentPoolFiltersFormValues) => {
    console.log(data);
    onSubmit({
      values: data,
      formInstance: methods,
    });
  };

  useEffect(() => {
    if (defaultValue && defaultValue.talentpool) {
      setValue('jobs', defaultValue.talentpool.jobOrderId);
      setValue('recruiter', defaultValue.talentpool.recruiterId);
    }
  }, [defaultValue?.talentpool]);

  const [anchorEl, setAnchorEl] = useState<{ [key: string]: Element | null }>({});

  const handleOpen = (event: React.MouseEvent<HTMLDivElement>, element: string) => {
    switch (element) {
      case 'clients':
        if (clientOptions.length === 0) fetchClients();
        break;
      case 'recruiter':
        if (recruitersOptions.length === 0) fetchRecruiters();
        break;
      case 'title':
        if (jobTypesOptions.length === 0) fetchJobTypes();
        break;
      case 'location':
        if (locationsOptions.length === 0) fetchLocations();
        break;
      case 'jobs':
        if (jobsOptions.length === 0) fetchJobs();
        break;
      case 'consultancy':
        if (consultanciesOptions.length === 0) fetchConsultancies();
        break;
      case 'status':
        if (statusesOptions.length === 0) fetchStatuses();
        break;
      case 'source':
        if (sourcesOptions.length === 0) fetchSources();
        break;
    }
    setAnchorEl({ [element]: event.currentTarget });
  };

  const handleClose = (element: string) => {
    setAnchorEl((prev) => ({ ...prev, [element]: null }));
  };

  const clients = methods.watch('clients');
  const selectedClients = useMemo(() => clientOptions.find((clientOption) => clients === clientOption.value), [clients]);

  const jobs = methods.watch('jobs');
  const selectedJob = useMemo(() => jobsOptions.find((jobOption) => jobs === jobOption.value), [jobs]);

  const source = methods.watch('source');
  const selectedSource = useMemo(() => sourcesOptions.find((sourcesOption) => source === sourcesOption.value), [source]);

  const jobTitle = methods.watch('title');
  const selectedJobTitle = useMemo(() => jobTypesOptions.find((job) => jobTitle === job.value), [jobTitle]);

  const consultancy = methods.watch('consultancy');
  const selectedConsultancy = useMemo(() => consultanciesOptions.find((consultancyOption) => consultancy === consultancyOption.value), [consultancy]);

  const recruiter = methods.watch('recruiter');
  const selectedRecruiter = useMemo(() => recruitersOptions.find((recruiterOption) => recruiter === recruiterOption.value), [recruiter]);
  
  const location = methods.watch('location');
  const selectedLocation = useMemo(() => locationsOptions.find((locationOption) => location === locationOption.value), [location]);

  const status = methods.watch('status');
  const selectedStatus = useMemo(() => statusesOptions.find((statusOption) => status === statusOption.value), [status]);
  return (
    <>
      <form onSubmit={handleSubmit(submitHandler)} className=" mt-4 rounded-md custom-shadow bg-white">
        <div className="flex w-full items-end gap-2 py-2 p-2">
          <div className="flex-1">
            <RHFInput name="searchQuery" control={methods.control} placeholder="Search" />
          </div>
          <div className="flex gap-2 items-center">
            <Button variant="contained" type="submit">Search</Button>
            <Button
              variant="outlined"
              type="reset"
              onClick={() => {
                methods.reset({});
                onReset();
              }}
            >
              Reset
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-11 gap-2 w-full p-2 pb-2">
          <div
            onMouseEnter={(e) => handleOpen(e, 'date')}
            className="flex cursor-pointer justify-between px-2 rounded-md relative py-1 items-center gap-2 text-sm">
            <span className="overflow-hidden text-ellipsis text-md">Date</span>
            <BiChevronDown className={twMerge(Boolean('date' in anchorEl && anchorEl['date']) && "rotate-180")} />
            {methods.watch('dateRange') && (
              <div className="h-[2px] absolute left-2 right-2 bottom-0 bg-primary" />
            )}

            <PopoverElement
              anchorEl={'date' in anchorEl ? anchorEl['date'] : null}
              open={Boolean('date' in anchorEl && anchorEl['date'])}
              handleClose={handleClose}
              element="date"
            >
              <Controller
                control={methods.control}
                name="dateRange"
                render={({ field }) => (
                  <RangeDatePicker
                    customCssClass="select-shadow"
                    locale={{
                      ...enUS,
                      options: {
                        weekStartsOn: 1,
                      },
                    }}
                    value={field.value}
                    open
                    onRangeChange={(e) => {
                      field.onChange(e);
                      handleClose('date');
                    }}
                  />
                )}
              />
            </PopoverElement>
          </div>
          <div
            onMouseEnter={(e) => handleOpen(e, 'clients')}
            onMouseLeave={(e) => handleClose('clients')}
            className="flex cursor-pointer justify-between px-2 rounded-md relative py-1 items-center gap-2 text-sm">
            <span className="overflow-hidden text-ellipsis text-md">Client</span>
            <BiChevronDown className={twMerge(Boolean('clients' in anchorEl && anchorEl['clients']) && "rotate-180")} />
            {methods.watch('clients') && methods.watch('clients').length > 0 && (
              <div className="h-[2px] absolute left-2 right-2 bottom-0 bg-primary" />
            )}

            <PopoverElement
              anchorEl={'clients' in anchorEl ? anchorEl['clients'] : null}
              open={Boolean('clients' in anchorEl && anchorEl['clients'])}
              handleClose={handleClose}
              element="clients"
            >
              <div className="min-w-[200px]">
                <RHFAutoComplete
                  name="clients"
                  control={methods.control}
                  options={clientOptions || []}
                  loading={clientsLoading}
                  placeholder="Search clients"
                  onSelectClose={() => handleClose('clients')}
                />
              </div>
            </PopoverElement>
          </div>
          <div
            onMouseEnter={(e) => handleOpen(e, 'jobs')}
            onMouseLeave={(e) => handleClose('jobs')}
            className="flex cursor-pointer justify-between px-2 rounded-md relative py-1 items-center gap-2 text-sm">
            <span className="overflow-hidden text-ellipsis text-md">Jobs</span>
            <BiChevronDown className={twMerge(Boolean('jobs' in anchorEl && anchorEl['jobs']) && "rotate-180")} />
            {methods.watch('jobs') && (
              <div className="h-[2px] absolute left-2 right-2 bottom-0 bg-primary" />
            )}

            <PopoverElement
              anchorEl={'jobs' in anchorEl ? anchorEl['jobs'] : null}
              open={Boolean('jobs' in anchorEl && anchorEl['jobs'])}
              handleClose={handleClose}
              element="jobs"
            >
              <div className="min-w-[200px]">
                <RHFAutoComplete
                  name="jobs"
                  control={methods.control}
                  options={jobsOptions}
                  loading={jobsLoading}
                  placeholder="Search jobs"
                  onSelectClose={() => handleClose('jobs')}
                />
              </div>
            </PopoverElement>
          </div>
          <div
            onMouseEnter={(e) => handleOpen(e, 'source')}
            onMouseLeave={(e) => handleClose('source')}
            className="flex cursor-pointer justify-between px-2 rounded-md relative py-1 items-center gap-2 text-sm">
            <span className="overflow-hidden text-ellipsis text-md">Source</span>
            <BiChevronDown className={twMerge(Boolean('source' in anchorEl && anchorEl['source']) && "rotate-180")} />
            {methods.watch('source') && (
              <div className="h-[2px] absolute left-2 right-2 bottom-0 bg-primary" />
            )}

            <PopoverElement
              anchorEl={'source' in anchorEl ? anchorEl['source'] : null}
              open={Boolean('source' in anchorEl && anchorEl['source'])}
              handleClose={handleClose}
              element="source"
            >
              <div className="min-w-[200px]">
                <RHFAutoComplete
                  name="source"
                  control={methods.control}
                  options={sourcesOptions}
                  placeholder="Search source"
                  onSelectClose={() => handleClose('source')}
                  loading={sourcesLoading}
                />
              </div>
            </PopoverElement>
          </div>
          <div
            onMouseEnter={(e) => handleOpen(e, 'title')}
            onMouseLeave={(e) => handleClose('title')}
            className="flex cursor-pointer justify-between px-2 rounded-md relative py-1 items-center gap-2 text-sm">
            <span className="overflow-hidden text-ellipsis text-md">Title</span>
            <BiChevronDown className={twMerge(Boolean('title' in anchorEl && anchorEl['title']) && "rotate-180")} />
            {methods.watch('title') && (
              <div className="h-[2px] absolute left-2 right-2 bottom-0 bg-primary" />
            )}

            <PopoverElement
              anchorEl={'title' in anchorEl ? anchorEl['title'] : null}
              open={Boolean('title' in anchorEl && anchorEl['title'])}
              handleClose={handleClose}
              element="title"
            >
              <div className="min-w-[200px]">
                <RHFAutoComplete
                  name="title"
                  control={methods.control}
                  options={jobTypesOptions}
                  loading={jonTypesLoading}
                  placeholder="Search title"
                  onSelectClose={() => handleClose('title')}
                />
              </div>
            </PopoverElement>
          </div>
          <div
            onMouseEnter={(e) => handleOpen(e, 'email')}
            onMouseLeave={(e) => handleClose('email')}
            className="flex cursor-pointer justify-between px-2 rounded-md relative py-1 items-center gap-2 text-sm">
            <span className="overflow-hidden text-ellipsis text-md">Email</span>
            <BiChevronDown className={twMerge(Boolean('email' in anchorEl && anchorEl['email']) && "rotate-180")} />
            {methods.watch('email') && (
              <div className="h-[2px] absolute left-2 right-2 bottom-0 bg-primary" />
            )}

            <PopoverElement
              anchorEl={'email' in anchorEl ? anchorEl['email'] : null}
              open={Boolean('email' in anchorEl && anchorEl['email'])}
              handleClose={handleClose}
              element="email"
            >
              <div className="min-w-[200px]">
                <RHFInput
                  name="email"
                  control={methods.control}
                  placeholder="Search email"
                />
              </div>
            </PopoverElement>
          </div>
          <div
            onMouseEnter={(e) => handleOpen(e, 'consultancy')}
            onMouseLeave={(e) => handleClose('consultancy')}
            className="flex cursor-pointer justify-between px-2 rounded-md relative py-1 items-center gap-2 text-sm"
          >
            <span className="overflow-hidden text-ellipsis text-md">Consultancy</span>
            <BiChevronDown className={twMerge(Boolean('consultancy' in anchorEl && anchorEl['consultancy']) && "rotate-180")} />
            {methods.watch('consultancy') && (
              <div className="h-[2px] absolute left-2 right-2 bottom-0 bg-primary" />
            )}

            <PopoverElement
              anchorEl={'consultancy' in anchorEl ? anchorEl['consultancy'] : null}
              open={Boolean('consultancy' in anchorEl && anchorEl['consultancy'])}
              handleClose={handleClose}
              element="consultancy"
            >
              <div className="min-w-[200px]">
                <RHFAutoComplete
                  name="consultancy"
                  control={methods.control}
                  options={consultanciesOptions}
                  loading={consultanciesLoading}
                  placeholder="Search consultancy"
                />
              </div>
            </PopoverElement>
          </div>
          <div
            onMouseEnter={(e) => handleOpen(e, 'location')}
            onMouseLeave={(e) => handleClose('location')}
            className="flex cursor-pointer justify-between px-2 rounded-md relative py-1 items-center gap-2 text-sm"
          >
            <span className="overflow-hidden text-ellipsis text-md">Location</span>
            <BiChevronDown className={twMerge(Boolean('location' in anchorEl && anchorEl['location']) && "rotate-180")} />
            {methods.watch('location') && (
              <div className="h-[2px] absolute left-2 right-2 bottom-0 bg-primary" />
            )}

            <PopoverElement
              anchorEl={'location' in anchorEl ? anchorEl['location'] : null}
              open={Boolean('location' in anchorEl && anchorEl['location'])}
              handleClose={handleClose}
              element="location"
            >
              <div className="min-w-[200px]">
                <RHFAutoComplete
                  name="location"
                  control={methods.control}
                  loading={locationsLoading}
                  options={locationsOptions}
                  placeholder="Search location"
                />
              </div>
            </PopoverElement>
          </div>
          <div
            onMouseEnter={(e) => handleOpen(e, 'recruiter')}
            onMouseLeave={(e) => handleClose('recruiter')}
            className="flex cursor-pointer justify-between px-2 rounded-md relative py-1 items-center gap-2 text-sm"
          >
            <span className="overflow-hidden text-ellipsis text-md">Recruiter</span>
            <BiChevronDown className={twMerge(Boolean('recruiter' in anchorEl && anchorEl['recruiter']) && "rotate-180")} />
            {methods.watch('recruiter') && (
              <div className="h-[2px] absolute left-2 right-2 bottom-0 bg-primary" />
            )}

            <PopoverElement
              anchorEl={'recruiter' in anchorEl ? anchorEl['recruiter'] : null}
              open={Boolean('recruiter' in anchorEl && anchorEl['recruiter'])}
              handleClose={handleClose}
              element="recruiter"
            >
              <div className="min-w-[200px]">
                <RHFAutoComplete
                  name="recruiter"
                  control={methods.control}
                  options={recruitersOptions}
                  loading={recruitersLoading}
                  placeholder="Search recruiter"
                />
              </div>
            </PopoverElement>
          </div>
          <div
            onMouseEnter={(e) => handleOpen(e, 'scores')}
            onMouseLeave={(e) => handleClose('scores')}
            className="flex cursor-pointer justify-between px-2 rounded-md relative py-1 items-center gap-2 text-sm"
          >
            <span className="overflow-hidden text-ellipsis text-md">Scores</span>
            <BiChevronDown className={twMerge(Boolean('scores' in anchorEl && anchorEl['scores']) && "rotate-180")} />
            {methods.watch('scores')
              && (
                (methods.watch('scores.pannaScoreLive.min') !== undefined && methods.watch('scores.pannaScoreLive.max') !== undefined)
                || (methods.watch('scores.pannaScore.min') !== undefined && methods.watch('scores.pannaScore.max') !== undefined)
              )
              && (
              <div className="h-[2px] absolute left-2 right-2 bottom-0 bg-primary" />
            )}
            <PopoverElement
              anchorEl={'scores' in anchorEl ? anchorEl['scores'] : null}
              open={Boolean('scores' in anchorEl && anchorEl['scores'])}
              handleClose={handleClose}
              element="scores"
            >
              <div className="min-w-[200px] min-h-[100px] items-center justify-center flex gap-4">
                <div className="flex flex-col gap-2">
                  <span className="text-sm">Paññã Score:</span>
                  <div className="flex gap-4">
                    <RHFSelect
                      control={methods.control}
                      name="scores.pannaScore.min"
                      options={pannaScoreOptions.filter((x) => x.value <= (methods.watch('scores.pannaScore.max') || 100))}
                      onSelectChange={(e) => {
                        const max = methods.getValues('scores.pannaScore.max') || 100;
                        if (e > max) {
                          methods.setValue('scores.pannaScore.max', e);
                        }
                      }}
                    />
                    <RHFSelect
                      control={methods.control}
                      name="scores.pannaScore.max"
                      options={pannaScoreOptions.filter((x) => x.value >= (methods.watch('scores.pannaScore.min') || 0))}
                      onSelectChange={(e) => {
                        const max = methods.getValues('scores.pannaScore.min') || 0;
                        if (e < max) {
                          methods.setValue('scores.pannaScore.min', e);
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <span className="text-sm">Paññã Live Rating:</span>
                  <div className="flex gap-4">
                    <Controller
                     control={methods.control}
                     name="scores.pannaScoreLive.min"
                     render={({ field }) => (
                      <div className="flex flex-col">
                        <Rating
                          onChange={(e, value) => {
                            const max = methods.getValues('scores.pannaScoreLive.max') || 5;
                            if (value && value > max) {
                              methods.setValue('scores.pannaScoreLive.max', value);
                            }
                            field.onChange(value)
                          }}
                          value={field.value}
                          size="small"
                          precision={0.5}
                        />
                        <span className="uppercase text-xs">Min</span>
                      </div>
                     )}
                    />
                    <Controller
                     control={methods.control}
                     name="scores.pannaScoreLive.max"
                     render={({ field }) => (
                      <div className="flex flex-col">
                        <Rating
                           onChange={(e, value) => {
                            const min = methods.getValues('scores.pannaScoreLive.min') || 0;
                            if (value && value < min) {
                              methods.setValue('scores.pannaScoreLive.min', value);
                            }
                            field.onChange(value)
                          }}
                          value={field.value}
                          size="small"
                          precision={0.5}
                        />
                        <span className="uppercase text-xs">Max</span>
                      </div>
                     )}
                    />
                  </div>
                </div>
              </div>
            </PopoverElement>
          </div>
          <div
            onMouseEnter={(e) => handleOpen(e, 'status')}
            onMouseLeave={(e) => handleClose('status')}
            className="flex cursor-pointer justify-between px-2 rounded-md relative py-1 items-center gap-2 text-sm"
          >
            <span className="overflow-hidden text-ellipsis text-md">Status</span>
            <BiChevronDown className={twMerge(Boolean('status' in anchorEl && anchorEl['status']) && "rotate-180")} />
            {methods.watch('status') && (
              <div className="h-[2px] absolute left-2 right-2 bottom-0 bg-primary" />
            )}

            <PopoverElement
              anchorEl={'status' in anchorEl ? anchorEl['status'] : null}
              open={Boolean('status' in anchorEl && anchorEl['status'])}
              handleClose={handleClose}
              element="status"
            >
              <div className="min-w-[300px]">
                <RHFAutoComplete
                  name="status"
                  control={methods.control}
                  options={statusesOptions}
                  loading={statusesLoading}
                  placeholder="Search status"
                />
              </div>
            </PopoverElement>
          </div>
        </div>
      </form>
      <div className="flex py-2 gap-2 flex-wrap">
        {methods.watch('dateRange') && (
          <Chip
            label={`${moment(methods.watch('dateRange')?.startDate).format('MM-DD-YYYY')} - ${moment(methods.watch('dateRange')?.endDate).format('MM-DD-YYYY')}`}
            onDelete={() => {
              methods.setValue('dateRange', undefined);
            }}
          />
        )}
        {selectedClients && (
          <Chip
            label={`${selectedClients.label}`}
            onDelete={() => {
              methods.setValue('clients', undefined);
            }}
          />
        )}
        {selectedJob && (
          <Chip
            label={`${selectedJob.label}`}
            onDelete={() => {
              methods.setValue('jobs', undefined);
            }}
          />
        )}
        {selectedSource && (
          <Chip
            label={`${selectedSource.label}`}
            onDelete={() => {
              methods.setValue('source', undefined);
            }}
          />
        )}
        {selectedJobTitle && (
          <Chip
            label={`${selectedJobTitle.label}`}
            onDelete={() => {
              methods.setValue('title', undefined);
            }}
          />
        )}
        {methods.watch('email') && (
          <Chip
            label={`${methods.watch('email')}`}
            onDelete={() => {
              methods.setValue('email', "");
            }}
          />
        )}
        {selectedConsultancy && (
          <Chip
            label={`Consultancy: ${selectedConsultancy.label}`}
            onDelete={() => {
              methods.setValue('consultancy', undefined);
            }}
          />
        )}
        {selectedLocation && (
          <Chip
          label={`${selectedLocation.label}`}
            onDelete={() => {
              methods.setValue('location', undefined);
            }}
          />
        )}
        {selectedRecruiter && (
          <Chip
            label={`${selectedRecruiter.label}`}
            onDelete={() => {
              methods.setValue('recruiter', undefined);
            }}
          />
        )}
        {selectedStatus && (
          <Chip
            label={`Status: ${selectedStatus.label}`}
            onDelete={() => {
              methods.setValue('status', undefined);
            }}
          />
        )}
        {methods.watch('scores.pannaScore.min') !== undefined &&
        methods.watch('scores.pannaScore.max') !== undefined ? (
          <>
           <Chip
            label={`Paññã score: ${methods.watch('scores.pannaScore.min')} - ${methods.watch('scores.pannaScore.max')}`}
            onDelete={() => {
              methods.setValue('scores.pannaScore.max', undefined);
              methods.setValue('scores.pannaScore.min', undefined);
            }}
          />
          </>
        ): null}
         {methods.watch('scores.pannaScoreLive.min') !== undefined &&
        methods.watch('scores.pannaScoreLive.max') !== undefined ? (
          <>
           <Chip
            label={`Paññã live score: ${methods.watch('scores.pannaScoreLive.min')} - ${methods.watch('scores.pannaScoreLive.max')}`}
            onDelete={() => {
              methods.setValue('scores.pannaScoreLive.max', undefined);
              methods.setValue('scores.pannaScoreLive.min', undefined);
            }}
          />
          </>
        ): null}
        {methods.watch('jobs')?.value}
      </div>
    </>
  );
};

TalentPoolFilters.defaultProps = {

};

TalentPoolFilters.propTypes = {

};

export default TalentPoolFilters;
